// pages/UserNotifications.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import {
  BellIcon,
  ArrowDownTrayIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import notificationService from "../services/notificationService";

const UserNotifications: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      loadNotifications();
    }
  }, [user]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const response = await notificationService.getUserNotifications();
      setNotifications(response.data || []);
      setUnreadCount(response.unread_count || 0);
    } catch (error) {
      console.error("Failed to load notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id: number) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id
            ? { ...n, read: true, read_at: new Date().toISOString() }
            : n,
        ),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark as read:", error);
    }
  };

  const dismissNotification = async (id: number) => {
    try {
      await notificationService.dismissNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to dismiss notification:", error);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const rows = notifications.map((n) => {
      const title = n.notification?.title || n.title || "";
      const message = n.notification?.message || n.message || "";
      const date = n.created_at || n.notification?.sent_at || "";
      const amount = getAmount(n);
      return [
        date,
        `${title} - ${message}`,
        amount !== null ? amount.toFixed(2) : "",
      ];
    });
    const header = ["Date", "Description", "Amount (USD)"];
    const csv = [header, ...rows]
      .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "notifications.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getAmount = (notification: any): number | null => {
    const metadata =
      notification.notification?.metadata || notification.metadata;
    const amount = metadata?.amount;
    if (amount === undefined || amount === null || isNaN(Number(amount))) {
      return null;
    }
    return Number(amount);
  };

  const formatAmount = (amount: number | null) => {
    if (amount === null) return "—";
    const formatted = Math.abs(amount).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return amount < 0 ? `-$${formatted}` : `$${formatted}`;
  };

  const totalAmount = notifications.reduce((sum, n) => {
    const amount = getAmount(n);
    return amount !== null ? sum + amount : sum;
  }, 0);

  const formatDate = (dateString: string) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const currentRangeLabel = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const fmt = (d: Date) =>
      d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${fmt(start)} – ${end.getDate()}, ${end.getFullYear()}`;
  };

  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto py-8 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {/* Header */}
          <div className="px-6 py-5 flex items-center justify-between">
            <h1 className="text-2xl font-normal text-gray-900">
              {currentRangeLabel()}
            </h1>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleDownload}
                title="Download"
                className="text-gray-700 hover:text-gray-900"
              >
                <ArrowDownTrayIcon className="h-6 w-6" />
              </button>
              <button
                onClick={handlePrint}
                title="Print"
                className="text-gray-700 hover:text-gray-900"
              >
                <PrinterIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          <div className="border-t border-gray-200" />

          {/* Summary row */}
          <div className="px-6 py-5 flex items-center justify-between">
            <span className="text-sm text-gray-500">
              Unread notifications: {unreadCount}
            </span>
            <span className="text-lg text-gray-900">
              Total: {formatAmount(totalAmount)}
            </span>
          </div>

          <div className="border-t border-gray-200" />

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-800 mx-auto"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-16">
              <BellIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">You're all caught up!</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-t border-gray-200">
                  <th className="text-left text-sm font-normal text-gray-500 px-6 py-4 w-40">
                    Date
                  </th>
                  <th className="text-left text-sm font-normal text-gray-500 px-6 py-4">
                    Description
                  </th>
                  <th className="text-right text-sm font-normal text-gray-500 px-6 py-4 w-40">
                    Amount (USD)
                  </th>
                </tr>
              </thead>
              <tbody>
                {notifications.map((notification) => {
                  const title =
                    notification.notification?.title || notification.title;
                  const message =
                    notification.notification?.message || notification.message;
                  const date =
                    notification.created_at ||
                    notification.notification?.sent_at;
                  const amount = getAmount(notification);

                  return (
                    <tr
                      key={notification.id}
                      onClick={() =>
                        !notification.read && markAsRead(notification.id)
                      }
                      className={`border-t border-gray-200 hover:bg-gray-50 cursor-pointer group ${
                        !notification.read ? "bg-blue-50/40" : ""
                      }`}
                    >
                      <td className="px-6 py-5 align-top text-gray-900">
                        {formatDate(date)}
                      </td>
                      <td className="px-6 py-5 align-top">
                        <p
                          className={`text-gray-900 ${
                            !notification.read ? "font-medium" : ""
                          }`}
                        >
                          {title}
                        </p>
                        <p className="text-gray-500 text-sm mt-1">{message}</p>
                        {!notification.read && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              dismissNotification(notification.id);
                            }}
                            className="text-xs text-gray-400 hover:text-gray-700 mt-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            Dismiss
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-5 align-top text-right font-medium text-gray-900">
                        {formatAmount(amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserNotifications;
